import { useState, useEffect } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Card,
  Group,
  Switch,
  Select,
  Button,
  Badge,
  Alert,
  Skeleton,
  Divider,
  Grid,
  TextInput,
  PasswordInput,
  Loader,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconRefresh,
  IconPlayerPlay,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconDatabase,
  IconServer,
  IconPlugConnected,
} from '@tabler/icons-react';

import { useSyncSchedule, useUpdateSyncSchedule, useTriggerSync } from '@/hooks';
import { useFogSettings, useUpdateFogSettings, useTestFogConnection } from '@/hooks/useSettings';

export function SettingsPage() {
  return (
    <Stack gap="lg">
      {/* Page header */}
      <div>
        <Title order={2}>Settings</Title>
        <Text c="dimmed" size="sm">
          Configure DeploySense system settings
        </Text>
      </div>

      {/* FOG Configuration Section */}
      <FogConfigurationSettings />

      {/* Sync Schedule Section */}
      <SyncScheduleSettings />
    </Stack>
  );
}

function FogConfigurationSettings() {
  const { data: settings, isLoading, isError, refetch } = useFogSettings();
  const updateSettings = useUpdateFogSettings();
  const testConnection = useTestFogConnection();

  // Local form state
  const [fogBaseUrl, setFogBaseUrl] = useState('');
  const [fogApiToken, setFogApiToken] = useState('');
  const [fogUserToken, setFogUserToken] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    tested: boolean;
    success: boolean;
    message: string;
    version?: string;
    hostCount?: number;
  } | null>(null);

  // Initialize form from loaded data
  useEffect(() => {
    if (settings) {
      setFogBaseUrl(settings.fogBaseUrl || '');
      setFogApiToken(settings.fogApiToken || '');
      setFogUserToken(settings.fogUserToken || '');
      setHasChanges(false);
    }
  }, [settings]);

  // Track changes
  useEffect(() => {
    if (settings) {
      const changed =
        fogBaseUrl !== (settings.fogBaseUrl || '') ||
        (fogApiToken !== '' && fogApiToken !== '********') ||
        (fogUserToken !== '' && fogUserToken !== '********');
      setHasChanges(changed);
    }
  }, [fogBaseUrl, fogApiToken, fogUserToken, settings]);

  // Test connection
  const handleTestConnection = async () => {
    setConnectionStatus(null);
    try {
      const result = await testConnection.mutateAsync({
        fogBaseUrl,
        fogApiToken: fogApiToken !== '********' ? fogApiToken : undefined,
        fogUserToken: fogUserToken !== '********' ? fogUserToken : undefined,
      });
      setConnectionStatus({
        tested: true,
        success: result.success,
        message: result.success ? (result.message || 'Connection successful') : (result.error || 'Connection failed'),
        version: result.fogVersion,
        hostCount: result.hostCount,
      });
      if (result.success) {
        notifications.show({
          title: 'Connection successful',
          message: `FOG Server v${result.fogVersion || 'Unknown'} - ${result.hostCount || 0} hosts`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        notifications.show({
          title: 'Connection failed',
          message: result.error || 'Could not connect to FOG server',
          color: 'red',
          icon: <IconX size={16} />,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setConnectionStatus({
        tested: true,
        success: false,
        message,
      });
      notifications.show({
        title: 'Connection failed',
        message,
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  // Save settings
  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        fogBaseUrl,
        fogApiToken: fogApiToken !== '********' && fogApiToken !== '' ? fogApiToken : undefined,
        fogUserToken: fogUserToken !== '********' && fogUserToken !== '' ? fogUserToken : undefined,
      });
      notifications.show({
        title: 'Settings saved',
        message: 'FOG configuration has been updated',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      // Reset token fields to masked value after save
      if (fogApiToken !== '********' && fogApiToken !== '') {
        setFogApiToken('********');
      }
      if (fogUserToken !== '********' && fogUserToken !== '') {
        setFogUserToken('********');
      }
      setHasChanges(false);
      refetch();
    } catch (error) {
      notifications.show({
        title: 'Failed to save',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  if (isLoading) {
    return (
      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Skeleton height={30} width={200} />
          <Skeleton height={100} />
        </Stack>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconX size={16} />} title="Error loading settings">
        Failed to load FOG configuration. Please try again.
      </Alert>
    );
  }

  return (
    <Paper p="lg" withBorder>
      <Stack gap="lg">
        {/* Section Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconServer size={24} />
            <div>
              <Title order={4}>FOG Server Configuration</Title>
              <Text size="sm" c="dimmed">
                Connect DeploySense to your FOG Project server
              </Text>
            </div>
          </Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Group>

        <Divider />

        {/* Connection Status */}
        {connectionStatus && connectionStatus.tested && (
          <Alert
            color={connectionStatus.success ? 'green' : 'red'}
            icon={connectionStatus.success ? <IconCheck size={16} /> : <IconX size={16} />}
            title={connectionStatus.success ? 'Connection Successful' : 'Connection Failed'}
          >
            {connectionStatus.message}
            {connectionStatus.success && connectionStatus.version && (
              <Text size="sm" mt="xs">
                FOG Version: {connectionStatus.version} | Hosts: {connectionStatus.hostCount || 0}
              </Text>
            )}
          </Alert>
        )}

        {/* Settings Form */}
        <Card withBorder>
          <Stack gap="md">
            <TextInput
              label="FOG Server URL"
              description="Base URL of your FOG server (e.g., http://192.168.1.100/fog)"
              placeholder="http://fog.example.com/fog"
              value={fogBaseUrl}
              onChange={(e) => setFogBaseUrl(e.currentTarget.value)}
            />

            <PasswordInput
              label="FOG API Token"
              description="API token from FOG Configuration > FOG Settings > API System"
              placeholder={settings?.hasApiToken ? 'Enter new token to change' : 'Enter API token'}
              value={fogApiToken}
              onChange={(e) => setFogApiToken(e.currentTarget.value)}
              onFocus={() => {
                if (fogApiToken === '********') setFogApiToken('');
              }}
            />

            <PasswordInput
              label="FOG User Token"
              description="User token from your FOG user profile > API Settings"
              placeholder={settings?.hasUserToken ? 'Enter new token to change' : 'Enter user token'}
              value={fogUserToken}
              onChange={(e) => setFogUserToken(e.currentTarget.value)}
              onFocus={() => {
                if (fogUserToken === '********') setFogUserToken('');
              }}
            />

            {hasChanges && (
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                You have unsaved changes
              </Alert>
            )}

            <Group justify="space-between">
              <Button
                variant="light"
                leftSection={testConnection.isPending ? <Loader size={16} /> : <IconPlugConnected size={16} />}
                onClick={handleTestConnection}
                loading={testConnection.isPending}
                disabled={!fogBaseUrl}
              >
                Test Connection
              </Button>

              <Group>
                <Button
                  variant="default"
                  onClick={() => {
                    if (settings) {
                      setFogBaseUrl(settings.fogBaseUrl || '');
                      setFogApiToken(settings.fogApiToken || '');
                      setFogUserToken(settings.fogUserToken || '');
                      setConnectionStatus(null);
                    }
                  }}
                  disabled={!hasChanges}
                >
                  Reset
                </Button>
                <Button
                  onClick={handleSave}
                  loading={updateSettings.isPending}
                  disabled={!hasChanges}
                >
                  Save Settings
                </Button>
              </Group>
            </Group>
          </Stack>
        </Card>

        {/* Current Configuration Status */}
        <Card withBorder bg="gray.0">
          <Group justify="space-between" mb="sm">
            <Text fw={500}>Configuration Status</Text>
            <Badge
              color={settings?.hasApiToken && settings?.hasUserToken ? 'green' : 'yellow'}
              variant="light"
            >
              {settings?.hasApiToken && settings?.hasUserToken ? 'Configured' : 'Incomplete'}
            </Badge>
          </Group>
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" c="dimmed">Server URL</Text>
              <Text size="sm" fw={500}>{settings?.fogBaseUrl || 'Not set'}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text size="sm" c="dimmed">API Token</Text>
              <Text size="sm" fw={500}>{settings?.hasApiToken ? 'Configured' : 'Not set'}</Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Text size="sm" c="dimmed">User Token</Text>
              <Text size="sm" fw={500}>{settings?.hasUserToken ? 'Configured' : 'Not set'}</Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Paper>
  );
}

function SyncScheduleSettings() {
  const { data: schedule, isLoading, isError, refetch } = useSyncSchedule();
  const updateSchedule = useUpdateSyncSchedule();
  const triggerSync = useTriggerSync();

  // Local form state
  const [enabled, setEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [syncType, setSyncType] = useState<'full' | 'incremental'>('incremental');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form from loaded data
  useEffect(() => {
    if (schedule) {
      setEnabled(schedule.enabled);
      setIntervalMinutes(schedule.intervalMinutes);
      setSyncType(schedule.syncType);
      setHasChanges(false);
    }
  }, [schedule]);

  // Track changes
  useEffect(() => {
    if (schedule) {
      const changed =
        enabled !== schedule.enabled ||
        intervalMinutes !== schedule.intervalMinutes ||
        syncType !== schedule.syncType;
      setHasChanges(changed);
    }
  }, [enabled, intervalMinutes, syncType, schedule]);

  // Save settings
  const handleSave = async () => {
    try {
      await updateSchedule.mutateAsync({
        enabled,
        intervalMinutes,
        syncType,
      });
      notifications.show({
        title: 'Settings saved',
        message: 'Sync schedule has been updated',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to save',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  // Trigger manual sync
  const handleTriggerSync = async () => {
    try {
      const result = await triggerSync.mutateAsync();
      if (result.success) {
        notifications.show({
          title: 'Sync completed',
          message: `${result.devicesProcessed || 0} devices processed in ${(result.durationSeconds || 0).toFixed(1)}s`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } else {
        notifications.show({
          title: 'Sync issue',
          message: result.message || 'Sync did not complete successfully',
          color: 'yellow',
          icon: <IconAlertTriangle size={16} />,
        });
      }
      refetch();
    } catch (error) {
      notifications.show({
        title: 'Sync failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) {
      // Future time
      const futureMins = Math.abs(diffMins);
      const futureHours = Math.floor(futureMins / 60);
      if (futureHours > 0) return `in ${futureHours}h ${futureMins % 60}m`;
      return `in ${futureMins}m`;
    }

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (isLoading) {
    return (
      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Skeleton height={30} width={200} />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </Stack>
      </Paper>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconX size={16} />} title="Error loading settings">
        Failed to load sync schedule settings. Please try again.
      </Alert>
    );
  }

  return (
    <Paper p="lg" withBorder>
      <Stack gap="lg">
        {/* Section Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconDatabase size={24} />
            <div>
              <Title order={4}>OCS Inventory Sync</Title>
              <Text size="sm" c="dimmed">
                Automatically sync device inventory from OCS
              </Text>
            </div>
          </Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Group>

        <Divider />

        {/* Current Status */}
        {schedule && (
          <Card withBorder bg="gray.0">
            <Group justify="space-between" mb="sm">
              <Text fw={500}>Current Status</Text>
              <Badge
                color={
                  schedule.isRunning
                    ? 'cyan'
                    : schedule.lastRunStatus === 'success'
                    ? 'green'
                    : schedule.lastRunStatus === 'failed'
                    ? 'red'
                    : 'gray'
                }
                variant="light"
              >
                {schedule.isRunning
                  ? 'Running'
                  : schedule.lastRunStatus || 'Never run'}
              </Badge>
            </Group>
            <Grid>
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">Last Run</Text>
                <Text size="sm" fw={500}>{formatRelativeTime(schedule.lastRunAt)}</Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">Next Run</Text>
                <Text size="sm" fw={500}>
                  {schedule.enabled ? formatRelativeTime(schedule.nextRunAt) : 'Disabled'}
                </Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">Devices Processed</Text>
                <Text size="sm" fw={500}>{schedule.lastRunDevicesProcessed ?? '-'}</Text>
              </Grid.Col>
            </Grid>
            {schedule.lastRunMessage && (
              <Text size="xs" c="dimmed" mt="sm">
                {schedule.lastRunMessage}
              </Text>
            )}
          </Card>
        )}

        {/* Settings Form */}
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={500}>Enable Scheduled Sync</Text>
                <Text size="sm" c="dimmed">
                  Automatically sync devices at regular intervals
                </Text>
              </div>
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.currentTarget.checked)}
                size="lg"
              />
            </Group>

            <Divider />

            <Group grow>
              <div>
                <Text size="sm" fw={500} mb="xs">
                  Sync Interval
                </Text>
                <Select
                  data={[
                    { value: '5', label: 'Every 5 minutes' },
                    { value: '15', label: 'Every 15 minutes' },
                    { value: '30', label: 'Every 30 minutes' },
                    { value: '60', label: 'Every hour' },
                    { value: '120', label: 'Every 2 hours' },
                    { value: '360', label: 'Every 6 hours' },
                    { value: '720', label: 'Every 12 hours' },
                    { value: '1440', label: 'Daily' },
                  ]}
                  value={String(intervalMinutes)}
                  onChange={(v) => setIntervalMinutes(Number(v))}
                  disabled={!enabled}
                />
              </div>

              <div>
                <Text size="sm" fw={500} mb="xs">
                  Sync Type
                </Text>
                <Select
                  data={[
                    { value: 'incremental', label: 'Incremental (faster)' },
                    { value: 'full', label: 'Full (all devices)' },
                  ]}
                  value={syncType}
                  onChange={(v) => setSyncType(v as 'full' | 'incremental')}
                  disabled={!enabled}
                />
              </div>
            </Group>

            {hasChanges && (
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                You have unsaved changes
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => {
                  if (schedule) {
                    setEnabled(schedule.enabled);
                    setIntervalMinutes(schedule.intervalMinutes);
                    setSyncType(schedule.syncType);
                  }
                }}
                disabled={!hasChanges}
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                loading={updateSchedule.isPending}
                disabled={!hasChanges}
              >
                Save Settings
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Manual Sync */}
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text fw={500}>Manual Sync</Text>
              <Text size="sm" c="dimmed">
                Trigger an immediate sync without waiting for the schedule
              </Text>
            </div>
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={handleTriggerSync}
              loading={triggerSync.isPending}
              disabled={schedule?.isRunning}
              color="green"
            >
              {schedule?.isRunning ? 'Sync Running...' : 'Run Sync Now'}
            </Button>
          </Group>
        </Card>
      </Stack>
    </Paper>
  );
}
